import React, { useState, useEffect, useRef } from 'react';
import { Responsive as ResponsiveGridLayout } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import { authHeaders } from '../utils/auth';

const DEFAULT_LAYOUT = [
    { i: 'properties', x: 0, y: 0, w: 3, h: 4 },
    { i: 'output', x: 0, y: 4, w: 3, h: 6 },
    { i: 'editor', x: 3, y: 0, w: 9, h: 10 }
];

const LANG_TEMPLATES: Record<string, string> = {
    python: `def example_skill(data):
    """Example tool for the agent"""
    result = f"Processed: {data}"
    print(result)
    return result

# Test invocation
if __name__ == "__main__":
    print(example_skill("test input"))`,
    javascript: `function exampleSkill(data) {
  // Example tool for the agent
  const result = \`Processed: \${data}\`;
  console.log(result);
  return result;
}

// Test invocation
console.log(exampleSkill("test input"));`,
    bash: `#!/bin/bash
# Example tool for the agent
DATA="$1"
echo "Processed: $DATA"`,
};

const LANG_MAP: Record<string, string> = {
    'Python Sandbox': 'python',
    'Node.js Runtime': 'javascript',
    'Bash Terminal': 'bash',
};

interface ExecResult {
    success: boolean;
    output?: string;
    error?: string;
    execution_time?: number;
    exit_code?: number;
}

export const SkillBuilderPanel: React.FC = () => {
    const [code, setCode] = useState(LANG_TEMPLATES.python);
    const [skillName, setSkillName] = useState('');
    const [skillDesc, setSkillDesc] = useState('');
    const [engine, setEngine] = useState('Python Sandbox');
    const [deployStatus, setDeployStatus] = useState<string | null>(null);
    const [execResult, setExecResult] = useState<ExecResult | null>(null);
    const [isRunning, setIsRunning] = useState(false);
    const [showOutput, setShowOutput] = useState(false);

    // Draggable Layout State
    const [isLocked, setIsLocked] = useState(true);
    const [layouts, setLayouts] = useState<any>({ lg: DEFAULT_LAYOUT });
    const [mounted, setMounted] = useState(false);
    
    // Custom width provider implementation
    const [width, setWidth] = useState(1200);
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        setMounted(true);
        const saved = localStorage.getItem('omnigate_skill_layout');
        if (saved) {
            try { const p = JSON.parse(saved);
                if (p) { Object.keys(p).forEach(k => { if (Array.isArray(p[k])) p[k].forEach((i:any) => delete i.static); }); }
                setLayouts(p); } catch { }
        }

        if (containerRef.current) {
            setWidth(containerRef.current.clientWidth);
        }

        const resizeObserver = new ResizeObserver(entries => {
            for (let entry of entries) {
                if (entry.contentRect.width > 0) {
                    setWidth(entry.contentRect.width);
                }
            }
        });

        if (containerRef.current) {
            resizeObserver.observe(containerRef.current);
        }

        return () => resizeObserver.disconnect();
    }, []);

    const onLayoutChange = (layout: any, allLayouts: any) => {
        setLayouts(allLayouts);
        localStorage.setItem('omnigate_skill_layout', JSON.stringify(allLayouts));
    };

    const handleEngineChange = (newEngine: string) => {
        setEngine(newEngine);
        const lang = LANG_MAP[newEngine] || 'python';
        setCode(LANG_TEMPLATES[lang] || '');
        setExecResult(null);
    };

    // 🔥 Real code execution via /api/code/execute
    const handleRun = async () => {
        setIsRunning(true);
        setExecResult(null);
        setShowOutput(true);
        try {
            const language = LANG_MAP[engine] || 'python';
            const res = await fetch('/api/code/execute', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...authHeaders() },
                body: JSON.stringify({ code, language, timeout: 30 })
            });
            const data = await res.json();
            setExecResult({
                success: data.success ?? (data.exit_code === 0),
                output: data.output || data.stdout || '',
                error: data.error || data.stderr || '',
                execution_time: data.execution_time,
                exit_code: data.exit_code ?? (data.success ? 0 : 1),
            });
        } catch (e: any) {
            setExecResult({ success: false, error: `Network error: ${e.message}` });
        } finally {
            setIsRunning(false);
        }
    };

    // Deploy as MCP skill entry
    const handleDeploy = async () => {
        if (!skillName) {
            setDeployStatus('❌ Set skill name first');
            return;
        }
        setDeployStatus('Deploying...');
        try {
            const res = await fetch('/v1/models', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...authHeaders() },
                body: JSON.stringify({
                    name: skillName,
                    endpoint: 'local://skill',
                    category: 'SKILL',
                    providerType: LANG_MAP[engine] || 'python',
                    parameters: { code, description: skillDesc }
                })
            });
            const data = await res.json();
            setDeployStatus(data.id ? `✅ Deployed: ${data.id}` : `❌ ${data.detail || JSON.stringify(data)}`);
        } catch (e) {
            setDeployStatus('❌ Deploy failed (network)');
        }
    };

    const langLabel = LANG_MAP[engine] || 'python';
    const fileExt: Record<string, string> = { python: '.py', javascript: '.js', bash: '.sh' };

    if (!mounted) return null;

    return (
        <div ref={containerRef} className="flex flex-col h-full bg-[#131722] overflow-hidden min-h-[600px] relative pb-10">
            <style>{`
                .react-grid-layout { position: relative; transition: height 200ms ease; }
                .react-grid-item { transition: all 200ms ease; transition-property: left, top; }
                .react-grid-item.cssTransforms { transition-property: transform; }
                .react-grid-item.resizing { z-index: 1; will-change: width, height; }
                .react-grid-item.react-draggable-dragging { transition: none; z-index: 3; will-change: transform; }
                .react-grid-item.react-grid-placeholder { background: rgba(34, 197, 94, 0.2); border: 2px dashed rgba(34, 197, 94, 0.5); opacity: 0.5; z-index: 2; transition: none; }
                .drag-handle { cursor: grab; }
                .drag-handle:active { cursor: grabbing; }
                .panel-wrapper { height: 100%; display: flex; flex-direction: column; background: #1e222d; border: 1px solid #2a2e39; border-radius: 0.5rem; overflow: hidden; box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.5); }
                .panel-wrapper:hover { border-color: rgba(34, 197, 94, 0.3); }
            `}</style>

            <div className="absolute -top-10 right-4 z-50 flex gap-2">
                <button onClick={() => setIsLocked(!isLocked)} className={`text-[9px] px-2 py-1 rounded border transition-colors ${isLocked ? 'bg-red-900/40 text-red-400 border-red-800' : 'bg-slate-800 text-slate-400 border-slate-700 hover:text-white'}`}>
                    <i className={`fas ${isLocked ? 'fa-lock' : 'fa-unlock'} mr-1`}></i> {isLocked ? 'Locked' : 'Unlocked'}
                </button>
                <button onClick={() => { setLayouts({ lg: DEFAULT_LAYOUT }); localStorage.removeItem('omnigate_skill_layout'); }} className="text-[9px] bg-slate-800 text-slate-400 hover:text-white px-2 py-1 rounded border border-slate-700">
                    <i className="fas fa-sync-alt mr-1"></i> Reset Layout
                </button>
            </div>

            
                <div className="flex justify-between items-center sm:mb-4 mb-2 px-2 bg-[#131722]/80 border border-[#2a2e39] rounded py-2">
                    <div className="flex items-center gap-2">
                        <button onClick={() => setIsLocked(!isLocked)} className={`text-[10px] px-3 py-1.5 rounded transition-all font-bold ${isLocked ? 'bg-red-500/20 text-red-400 border border-red-500/50 shadow-[0_0_10px_rgba(239,68,68,0.2)]' : 'bg-green-500/20 text-green-400 border border-green-500/50'}`}>
                            <i className={`fas ${isLocked ? 'fa-lock' : 'fa-unlock'} mr-1`}></i> {isLocked ? 'Blokada Włączona' : 'Odblokowane (Przeciągaj)'}
                        </button>
                    </div>
                </div>
    <ResponsiveGridLayout
                className="layout flex-1"
                width={width}
                layouts={isLocked ? Object.fromEntries(Object.entries(layouts).map(([k, v]) => [k, v.map((i: any) => ({ ...i, static: true }))])) : layouts}
                breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
                cols={{ lg: 12, md: 10, sm: 6, xs: 4, xxs: 2 }}
                rowHeight={50}
                onLayoutChange={onLayoutChange}
                // @ts-ignore
                isDraggable={!isLocked}
                // @ts-ignore
                isResizable={!isLocked}
                // @ts-ignore
                draggableHandle=".drag-handle"
                margin={[12, 12]}
            >
                {/* Panel 1: Properties */}
                <div key="properties" className="panel-wrapper">
                    <div className={`p-3 border-b border-[#2a2e39] bg-[#131722]/60 flex items-center justify-between ${!isLocked ? 'drag-handle' : 'cursor-default'}`}>
                        <div className="flex items-center gap-2">
                            <i className="fas fa-puzzle-piece text-green-500"></i>
                            <h2 className="text-[10px] font-black text-white uppercase tracking-widest">Skill Config</h2>
                        </div>
                        <i className="fas fa-grip-lines text-slate-600"></i>
                    </div>
                    <div className="flex-1 overflow-y-auto p-4 space-y-3">
                        <div>
                            <label className="text-[10px] font-bold text-slate-500 uppercase block mb-1">Skill Name</label>
                            <input
                                type="text"
                                value={skillName}
                                onChange={e => setSkillName(e.target.value)}
                                placeholder="e.g. fetch_stock_price"
                                className="w-full bg-[#131722] border border-[#2a2e39] text-slate-200 text-xs px-2 py-1.5 rounded focus:outline-none focus:border-green-500/50"
                            />
                        </div>
                        <div>
                            <label className="text-[10px] font-bold text-slate-500 uppercase block mb-1">Description</label>
                            <textarea
                                rows={3}
                                value={skillDesc}
                                onChange={e => setSkillDesc(e.target.value)}
                                placeholder="Co robi ten skill?"
                                className="w-full bg-[#131722] border border-[#2a2e39] text-slate-200 text-xs px-2 py-1.5 rounded focus:outline-none focus:border-green-500/50 resize-none"
                            />
                        </div>
                        <div>
                            <label className="text-[10px] font-bold text-slate-500 uppercase block mb-1">Runtime</label>
                            <select
                                value={engine}
                                onChange={e => handleEngineChange(e.target.value)}
                                className="w-full bg-[#131722] border border-[#2a2e39] text-slate-200 text-xs px-2 py-1.5 rounded focus:outline-none focus:border-green-500/50"
                            >
                                <option>Python Sandbox</option>
                                <option>Node.js Runtime</option>
                                <option>Bash Terminal</option>
                            </select>
                        </div>

                        <div className="pt-2 border-t border-[#2a2e39] mt-4">
                            {deployStatus && (
                                <div className={`text-[9px] px-2 py-1.5 rounded mb-2 w-full text-center ${deployStatus.startsWith('✅') ? 'text-green-400 bg-green-500/10' : deployStatus.startsWith('❌') ? 'text-red-400 bg-red-500/10' : 'text-yellow-400 bg-yellow-500/10'}`}>
                                    {deployStatus}
                                </div>
                            )}
                            <button onClick={handleDeploy} className="w-full text-[10px] bg-green-500/20 text-green-400 border border-green-500/30 px-3 py-2 rounded uppercase font-black hover:bg-green-500/30 transition-colors shadow-lg shadow-green-900/20">
                                <i className="fas fa-cloud-upload-alt mr-2"></i>Deploy Skill
                            </button>
                        </div>
                    </div>
                </div>

                {/* Panel 2: Output */}
                <div key="output" className="panel-wrapper">
                    <div className={`p-3 border-b border-[#2a2e39] bg-[#131722]/60 flex items-center justify-between ${!isLocked ? 'drag-handle' : 'cursor-default'}`}>
                        <div className="flex items-center gap-2">
                            <i className="fas fa-terminal text-slate-400"></i>
                            <h2 className="text-[10px] font-black text-white uppercase tracking-widest">Output</h2>
                        </div>
                        {execResult && (
                            <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${execResult.success ? 'text-green-400 bg-green-500/10' : 'text-red-400 bg-red-500/10'}`}>
                                {execResult.success ? '✅ OK' : '❌ Error'}
                                {execResult.execution_time !== undefined && ` ${(execResult.execution_time * 1000).toFixed(0)}ms`}
                            </span>
                        )}
                    </div>
                    <div className="flex-1 bg-[#0a0c10] p-3 text-[10px] font-mono overflow-y-auto custom-scrollbar">
                        {isRunning && <span className="text-yellow-400 animate-pulse">Running execution...</span>}
                        {execResult?.output && (
                            <pre className="text-green-400 whitespace-pre-wrap break-all">{execResult.output}</pre>
                        )}
                        {execResult?.error && (
                            <pre className="text-red-400 whitespace-pre-wrap break-all">{execResult.error}</pre>
                        )}
                        {!isRunning && !execResult && <span className="text-slate-600 flex items-center justify-center h-full text-center">Brak logów.<br />Uruchom skill aby zobaczyć wynik.</span>}
                    </div>
                </div>

                {/* Panel 3: Code Editor */}
                <div key="editor" className="panel-wrapper">
                    <div className={`bg-[#131722]/60 border-b border-[#2a2e39] px-3 py-2 flex justify-between items-center ${!isLocked ? 'drag-handle' : 'cursor-default'}`}>
                        <span className="text-slate-400 font-mono text-[10px]">
                            {langLabel === 'python' ? <i className="fab fa-python text-yellow-500 mr-2"></i>
                                : langLabel === 'javascript' ? <i className="fab fa-js-square text-yellow-400 mr-2"></i>
                                    : <i className="fas fa-terminal text-green-500 mr-2"></i>}
                            {skillName || 'new_skill'}{fileExt[langLabel] || '.py'}
                        </span>
                        <div className="flex items-center gap-3">
                            <span className="text-[9px] text-slate-600 font-mono">{code.split('\n').length} lines</span>
                            <button
                                onClick={handleRun}
                                disabled={isRunning}
                                className={`flex items-center gap-1 px-3 py-1 rounded uppercase font-bold text-[9px] transition-all shadow-md ${isRunning
                                    ? 'bg-yellow-500/10 text-yellow-400 cursor-wait'
                                    : 'bg-green-500/20 text-green-400 border border-green-500/30 hover:bg-green-500/30'
                                    }`}
                            >
                                <i className={`fas ${isRunning ? 'fa-spinner fa-spin' : 'fa-play'}`}></i>
                                {isRunning ? 'Running...' : 'Run'}
                            </button>
                        </div>
                    </div>
                    <textarea
                        value={code}
                        onChange={e => setCode(e.target.value)}
                        className="flex-1 w-full bg-[#0a0c10] text-green-400 p-4 text-xs font-mono focus:outline-none resize-none custom-scrollbar"
                        spellCheck={false}
                        style={{ tabSize: 4 }}
                        onKeyDown={e => {
                            if (e.key === 'Tab') {
                                e.preventDefault();
                                const start = e.currentTarget.selectionStart;
                                const end = e.currentTarget.selectionEnd;
                                const newCode = code.substring(0, start) + '    ' + code.substring(end);
                                setCode(newCode);
                                setTimeout(() => { e.currentTarget.selectionStart = e.currentTarget.selectionEnd = start + 4; }, 0);
                            }
                        }}
                    />
                </div>
            </ResponsiveGridLayout>
        </div>
    );
};
